import React from 'react';
import { useIntl } from 'react-intl';

import { Anchor, Box } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { messages } from '../utils/messages.ts';
import type { UpdateItem } from '../utils/recently-updated-types.ts';

import { ContentTypeIcon } from './ContentTypeIcon.tsx';

const styles = cssMap({
	listItem: {
		paddingBlock: token('space.100'),
	},
	sidebarListItem: {
		paddingBlock: token('space.100'),
	},
	sidebarMeta: {
		font: token('font.body.small'),
		color: token('color.text.subtlest'),
		marginBlockStart: token('space.050'),
		paddingInlineStart: token('space.250'),
		listStyle: 'circle',
	},
	titleRow: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.075'),
	},
	itemContent: {
		flex: '1',
	},
	itemMeta: {
		font: token('font.body.small'),
		color: token('color.text.subtlest'),
		marginBlockStart: token('space.050'),
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'center',
		gap: token('space.050'),
	},
	titleLink: {
		textDecoration: 'none',
	},
});

// ---------------------------------------------------------------------------
// Item meta (date + author / changes link)
// ---------------------------------------------------------------------------

type ItemMetaProps = {
	item: UpdateItem;
};

function ItemMeta({ item }: ItemMetaProps) {
	const { formatMessage } = useIntl();

	const dateText = item.formattedDate;
	const authorName = item.author.displayName;

	let authorActionPrefix;
	if (item.contentType === 'comment') {
		authorActionPrefix = formatMessage(messages.commentedByPrefix);
	} else if (item.contentType === 'attachment') {
		authorActionPrefix = formatMessage(messages.attachedByPrefix);
	} else {
		authorActionPrefix = formatMessage(messages.contributedByPrefix);
	}

	let authorElement = null;
	if (authorName) {
		authorElement = item.author.profileUrl ? (
			<Anchor href={item.author.profileUrl}>{authorName}</Anchor>
		) : (
			<>{authorName}</>
		);
	}

	return (
		<Box xcss={styles.itemMeta}>
			<Box as="span">{dateText}</Box>
			{authorElement && (
				<>
					<Box as="span" aria-hidden>
						•
					</Box>
					<Box as="span">
						{authorActionPrefix} {authorElement}
					</Box>
				</>
			)}
			{item.changesUrl && (
				<>
					<Box as="span" aria-hidden>
						•
					</Box>
					<Anchor href={item.changesUrl}>{formatMessage(messages.viewChanges)}</Anchor>
				</>
			)}
		</Box>
	);
}

// ---------------------------------------------------------------------------
// Concise update item
// ---------------------------------------------------------------------------

type FlatUpdateItemProps = {
	item: UpdateItem;
};

export function FlatUpdateItem({ item }: FlatUpdateItemProps) {
	return (
		<Box as="li" xcss={styles.listItem}>
			<Box xcss={styles.itemContent}>
				<Box xcss={styles.titleRow}>
					<ContentTypeIcon contentType={item.contentType} />
					<Anchor href={item.url} title={item.spaceName} xcss={styles.titleLink}>
						{item.title}
					</Anchor>
				</Box>
				<ItemMeta item={item} />
			</Box>
		</Box>
	);
}

// ---------------------------------------------------------------------------
// Sidebar update item
// ---------------------------------------------------------------------------

export function SidebarUpdateItem({ item }: FlatUpdateItemProps) {
	const { formatMessage } = useIntl();
	return (
		<Box as="li" xcss={styles.sidebarListItem}>
			<Box xcss={styles.itemContent}>
				<Box xcss={styles.titleRow}>
					<ContentTypeIcon contentType={item.contentType} />
					<Anchor href={item.url} title={item.spaceName} xcss={styles.titleLink}>
						{item.title}
					</Anchor>
				</Box>
				<Box as="ul" xcss={styles.sidebarMeta}>
					<Box as="li">{formatMessage(messages.contributedOn, { date: item.formattedDate })}</Box>
				</Box>
			</Box>
		</Box>
	);
}
